import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { assert } from '@ember/debug';
import { getOwner } from '@ember/owner';
import { service } from '@ember/service';
import { task } from 'ember-concurrency';
import { on } from '@ember/modifier';
import { t } from 'ember-intl';
import { or, not } from 'ember-truth-helpers';
// eslint-disable-next-line ember/no-at-ember-render-modifiers
import didInsert from '@ember/render-modifiers/modifiers/did-insert';
// eslint-disable-next-line ember/no-at-ember-render-modifiers
import didUpdate from '@ember/render-modifiers/modifiers/did-update';
import {
  HdsModal,
  HdsFormRadioGroup,
  HdsButtonSet,
  HdsButton,
} from '@hashicorp/design-system-components/components';
import { HashicorpCloudResourcemanagerPolicyBindingMemberType } from '@clients/cloud-resource-manager';
import PolicyManager from 'manage-access/utils/policy-manager';
import { supportTicketPrefillHref } from 'core/utils/support-ticket';

/**
 *
 * `IamGroupsModalsEditMemberRole` renders the modal for removing a member from an HCP Group
 *
 * ```
 * <Iam::Groups::Modals::EditMemberRole
 *  @group={{@group}}
 *  @groupPolicy={{@groupPolicy}}
 *  @memberToEditRole={{@memberToEditRole}}
 *  @isActive={{@isActive}}
 *  @closeModal={{@closeRoleModal}}
 *  @onPolicyUpdateSuccess={{this.onPolicyUpdateSuccess}}
 * />
 * ```
 *
 * @class IamGroupsModalsEditMemberRole
 *
 */

/**
 * `group` is an object that contains the HCP Group properties
 * @argument group
 * @type {Object}
 */

/**
 * `groupLevelPermissions` is a an array of actions the `memberToEditRole` is authorized to take on the group
 * @argument groupLevelPermissions
 * @type {Object}
 */

/**
 * `rawGroupPolicy` is a raw json representation of an HCP Group's updated policy
 * @argument rawGroupPolicy
 * @type {Object}
 */

/**
 * `groupPolicy` is a Policy Manager instance that contains the HCP Group's updated policy
 * @argument groupPolicy
 * @type {Object}
 */

/**
 * `memberToEditRole is an object that contains properties of the member whose role to edit for the HCP Group
 * @argument memberToEditRole
 * @type {Object}
 */

/**
 * This shows or hides the modal based on truthiness.
 * @argument isActive
 * @type {Boolean}
 */

/**
 * A callback function to be called on a successful removal of a group member or
 * owhen a user cancels the modal.
 * @argument closeModal
 * @type {Function}
 */
export default class IamGroupsModalsEditMemberRole extends Component {
  @service api;
  @service intl;
  @service flashMessages;
  @service router;
  @service userContext;

  @tracked GroupPolicy = new PolicyManager({
    policy: this.args.groupPolicy,
  });

  GROUP_MANAGER_ROLE_ID = 'roles/iam.group-manager';

  get isGroupManager() {
    const { roleIds = [] } =
      this.GroupPolicy.getRolesByMember({
        memberId: this.args.memberToEditRole.id,
        memberType: HashicorpCloudResourcemanagerPolicyBindingMemberType.USER,
      }) ?? {};
    return roleIds.includes(this.GROUP_MANAGER_ROLE_ID);
  }

  get supportTicketHref() {
    return supportTicketPrefillHref(this.userContext);
  }

  didUpdateGroupPolicy = () => {
    this.GroupPolicy = new PolicyManager({
      policy: this.args.groupPolicy,
    });
  };

  removeGroupManagerRole = () => {
    this.GroupPolicy.removeMemberFromRole(
      {
        memberId: this.args.memberToEditRole.id,
        memberType: HashicorpCloudResourcemanagerPolicyBindingMemberType.USER,
      },
      [this.GROUP_MANAGER_ROLE_ID]
    );
  };

  addGroupManagerRole = () => {
    this.GroupPolicy.addMemberToRole(
      {
        memberId: this.args.memberToEditRole.id,
        memberType: HashicorpCloudResourcemanagerPolicyBindingMemberType.USER,
      },
      [this.GROUP_MANAGER_ROLE_ID]
    );
  };

  updateGroupMemberRole = task(async (e) => {
    e.preventDefault();
    assert(
      'memberToEditRole is required',
      this.args.memberToEditRole !== undefined
    );

    const { group, memberToEditRole, closeModal, onPolicyUpdateSuccess } =
      this.args;
    const { name, email } = memberToEditRole;
    const { resourceName } = group;

    try {
      const payload = this.GroupPolicy.getRawPolicy();
      const { policy } =
        await this.api.resourceManager.resources.resourceServiceSetIamPolicy({
          policy: payload,
          resourceName,
        });

      if (policy) {
        if (
          onPolicyUpdateSuccess &&
          typeof onPolicyUpdateSuccess === 'function'
        ) {
          onPolicyUpdateSuccess(policy);
        }
        this.flashMessages.success(
          this.intl.t(
            'components.iam.groups.form.edit-member-role.success.title'
          ),
          {
            content: this.intl.t(
              'components.iam.groups.form.edit-member-role.success.message',
              {
                groupRole: this.isGroupManager
                  ? this.intl.t(
                      'components.iam.groups.form.edit-member-role.success.manager'
                    )
                  : this.intl.t(
                      'components.iam.groups.form.edit-member-role.success.member'
                    ),
                memberNameOrEmail: name || email,
                htmlSafe: true,
              }
            ),
          }
        );

        if (closeModal && typeof closeModal === 'function') {
          await this.args.closeModal();
        }
      }
    } catch {
      this.flashMessages.error(
        this.intl.t('components.iam.groups.form.edit-member-role.error.title'),
        {
          content: this.intl.t(
            'components.iam.groups.form.edit-member-role.error.message'
          ),
          linkIcon: 'help',
          linkIconPosition: 'leading',
          linkText: this.intl.t(
            'components.iam.groups.form.edit-member-role.error.support-button'
          ),
          linkUrl: this.supportTicketHref,
        }
      );
    }
    //refresh the Group detail route to update the model
    const refreshRoute = getOwner(this).lookup(
      'route:cloud.access-control.groups.detail'
    );
    await refreshRoute.refresh();
    return this.router.transitionTo('cloud.access-control.groups.detail');
  });

  <template>
    {{#if @isActive}}
      <HdsModal
        @onClose={{@closeModal}}
        data-test-iam-groups-modal-edit-member-role
        as |M|
      >
        <M.Header
          @tagline={{this.headerTagline}}
          data-test-iam-groups-modal-edit-member-role-header
        >
          {{t
            "components.iam.groups.modals.edit-member-role.header.title"
            memberNameOrEmail=(or
              @memberToEditRole.name @memberToEditRole.email
            )
            groupName=@group.displayName
            htmlSafe=true
          }}
        </M.Header>
        <M.Body>
          <form
            id="groups-members-modals-edit-member-role"
            {{on "submit" this.updateGroupMemberRole.perform}}
            {{didInsert this.didUpdateGroupPolicy}}
            {{didUpdate this.didUpdateGroupPolicy @groupPolicy}}
            novalidate
            data-test-iam-groups-modal-edit-member-role-body
          >
            <HdsFormRadioGroup @name="role" as |G|>
              <G.Legend>
                {{t
                  "components.iam.groups.modals.edit-member-role.body.subtitle"
                }}
              </G.Legend>
              <G.RadioField
                @value="Manager"
                checked={{this.isGroupManager}}
                data-test-iam-groups-modal-edit-member-role-radio="manager"
                {{on "change" this.addGroupManagerRole}}
                as |F|
              >
                <F.Label>{{t
                    "components.iam.groups.modals.edit-member-role.body.manager-radio.tagline"
                  }}</F.Label>
                <F.HelperText>
                  {{t
                    "components.iam.groups.modals.edit-member-role.body.manager-radio.description"
                  }}
                </F.HelperText>
              </G.RadioField>
              <G.RadioField
                @value="Member"
                checked={{not this.isGroupManager}}
                data-test-iam-groups-modal-edit-member-role-radio="member"
                {{on "change" this.removeGroupManagerRole}}
                as |F|
              >
                <F.Label>{{t
                    "components.iam.groups.modals.edit-member-role.body.member-radio.tagline"
                  }}</F.Label>
                <F.HelperText>
                  {{t
                    "components.iam.groups.modals.edit-member-role.body.member-radio.description"
                  }}
                </F.HelperText>
              </G.RadioField>
            </HdsFormRadioGroup>
          </form>
        </M.Body>
        <M.Footer>
          <HdsButtonSet>
            <HdsButton
              @color="primary"
              @text={{t
                "components.iam.groups.modals.edit-member-role.body.save-button"
              }}
              disabled={{this.updateGroupMemberRole.isRunning}}
              form="groups-members-modals-edit-member-role"
              type="submit"
              data-test-iam-groups-modal-edit-member-role-submit
            />
            <HdsButton
              @color="secondary"
              @text={{t "components.form.cancel"}}
              disabled={{this.updateGroupMemberRole.isRunning}}
              {{on "click" @closeModal}}
              data-test-iam-groups-modal-edit-member-role-cancel
            />
          </HdsButtonSet>
        </M.Footer>
      </HdsModal>
    {{/if}}
  </template>
}
